/**
 * Which form-library fragments the Copilot caption window renders.
 *
 * Copilot is an AeDrive concept, so the decision lives here rather than in the
 * shared graphic definitions — `v_config` carries facts about how a graphic is
 * USED (shortcuts, routing, scheduling), not how a host furnishes a window.
 * Sits beside copilotCaptionWidgets.js and copilotTabGroups.js, the other two
 * per-graphic Copilot maps.
 *
 * The composed chrome was proved equivalent to the pre-composition window with
 * every fragment on before any was switched off; an all-true profile still
 * reproduces that window exactly.
 */

/**
 * The stripped Copilot window (OPUS-1637): caption identity, the values going
 * to air, two actions.
 *
 * The keys take the show* shape the system already used for these exact
 * concepts: `showForm` and `showNavigator` were form-library props until 0.6.0
 * removed them ("host composes layout"), and 78 of 112 definitions still carry
 * them under `v_config.mos.ui`. Those entries are now vestigial — nothing
 * reads them, because the props they fed are gone — so this is inherited
 * vocabulary rather than a live contract. Worth inheriting anyway: the names
 * are the ones this system has always used for these two fragments, and
 * `v_config.copilot.ui` will sit beside that block. The other four follow the
 * same shape rather than inventing a second vocabulary for the same idea.
 *
 * - showHeader     — carries the settings cog, version string and library path
 * - showNavigator  — the caption choices the operator can SEE, drawn by the
 *                    library's own MaestroCaptionNavView. Hidden rather than
 *                    unmounted when off, because that component also runs
 *                    auto-selection and the DDS chain descent. A shortcut that
 *                    names a caption leaves nothing to choose anyway, so this
 *                    is for graphics that leave a choice open but should not
 *                    offer it in Copilot.
 * - showInputs     — the widgets: Added Time's numpad, the clock controls, the
 *                    substitution panel. These are how the operator sets a
 *                    value before firing, so they stay on; off makes those
 *                    captions confirm-only.
 * - showSummary    — the read-only label/value rows, and the overtype on them
 * - showActions    — the three promoted buttons and the press-status line
 * - showForm       — invites editing this window is not for
 *
 * Set any of these true to bring that fragment back; the arrangement needs no
 * change. All-true renders the pre-composition window, which is what the
 * composition was proved against before anything was hidden.
 */
export const DEFAULT_COPILOT_PROFILE = Object.freeze({
  showHeader: false,
  showNavigator: true,
  showInputs: true,
  showSummary: false,
  showActions: true,
  showForm: false,
});

/**
 * Per-graphic opt-outs from the default, keyed by graphic id.
 *
 * EMPTY IS THE BASELINE. With nothing here, Copilot surfaces the same caption
 * choices and the same caption inputs that drive does — the window is the
 * stripped chrome around identical content, not a different set of controls.
 * An entry is an opt-out a particular graphic has earned, and each one has to
 * be justified against what drive shows.
 *
 * Opt-outs are host-specific by nature: the same caption is right in drive and
 * wrong in Copilot only because the surfaces differ, never because the caption
 * changed. So they belong to the host, which is why they live here and not in
 * anything the form library reads.
 *
 * TEMPORARY HOME. These entries are destined for `v_config.copilot.ui` in the
 * graphic definition — it already exists, is `{}` on every graphic, and the
 * form library treats it as a host section it will not read. Authoring there
 * keeps Copilot behaviour in the definition beside the shortcuts, and leaves
 * drive untouched because nothing else reads that block. Living here first is
 * deliberate: a code edit and a hot reload is a faster loop than a definition
 * edit, a repackage and an upload while the set of graphics that need to
 * differ is still being discovered. The keys are already the profile's own, so
 * the move is lifting this object into the definition rather than a rewrite.
 *
 * Note this is NOT the same lever as pointing a copilot shortcut at a caption
 * (`type: "caption"`). That decides the caption CHOICES; this decides the
 * caption's INPUTS, which belong to the caption itself — proved by serving
 * Scoreline's definition with the shortcut repointed at the leaf, where both
 * of its inputs still rendered.
 */
export const COPILOT_PROFILE_BY_GRAPHIC = Object.freeze({});

/**
 * The profile for a graphic: the default, with any per-graphic keys layered on.
 * A fresh object each call, so a caller cannot mutate the shared default.
 */
export const copilotProfile = (graphicId, overrides = COPILOT_PROFILE_BY_GRAPHIC) => ({
  ...DEFAULT_COPILOT_PROFILE,
  ...(overrides[graphicId] || {}),
});
